import { Box, Stack } from '@mui/material';
import React, { forwardRef } from 'react';
import { LoadingButton } from '@mui/lab';
import { useFeatureFlags } from '@chronosphereio/core';
import { Button } from '../Button';
import { ModalBase, ModalBaseProps } from './ModalBase';

export interface ModalProps extends Omit<ModalBaseProps, 'footer'> {
  /**
   * Click handler for primary action button. If not provided, the primary button will not be rendered.
   */
  onPrimaryButtonClick?: () => void;
  /**
   * Whether the primary action button uses the danger variant.
   * @default false
   */
  primaryButtonDanger?: boolean;
  /**
   * Whether the primary action button is disabled.
   * @default false
   */
  primaryButtonDisabled?: boolean;
  /**
   * Whether the primary action button is loading. Uses a MUI LoadingButton component to prevent layout shift when
   * changing between loading and not loading states
   * @default false
   */
  primaryButtonLoading?: boolean;
  /**
   * Button text for primary action button for the modal. Renders as a themed primary button, right-aligned (unless
   * there is a secondary button, in which case the primary button is the left-most button). Text defaults to "OK" if
   * not specified
   */
  primaryButtonText?: string;
  /**
   * Button text for secondary action button. Secondary button is always present, cannot be disabled, and calls onClose
   * () on click. Renders as a themed outlined button as the right-most button in the modal. Text defaults to "Cancel"
   * if not specified
   */
  secondaryButtonText?: React.ReactNode;
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(function Modal(
  {
    children,
    primaryButtonDanger = false,
    primaryButtonDisabled = false,
    primaryButtonLoading = false,
    onClose,
    onPrimaryButtonClick,
    primaryButtonText,
    secondaryButtonText,
    ...rest
  },
  ref
) {
  const { panwRebrandStubYear } = useFeatureFlags();

  const primaryButton = onPrimaryButtonClick ? (
    <LoadingButton
      variant="contained"
      color={primaryButtonDanger ? 'error' : 'primary'}
      loading={primaryButtonLoading}
      onClick={onPrimaryButtonClick}
      disabled={primaryButtonDisabled}
    >
      {/*
        NB: here's a known issue with Chrome that requires wrapping the text in a span here. See https://mui.com/material-ui/react-button/#loading-button for more details
      */}
      <span>{primaryButtonText || 'Ok'}</span>
    </LoadingButton>
  ) : null;

  const secondaryButton = (
    <Button variant="secondary" onClick={onClose}>
      {secondaryButtonText || 'Cancel'}
    </Button>
  );

  return (
    <ModalBase
      onClose={onClose}
      ref={ref}
      Footer={
        <Box p={1} justifyContent="flex-end" width="100%" display="flex" flexDirection="row" alignItems="center">
          <Stack direction="row" spacing={1}>
            {panwRebrandStubYear ? (
              <>
                {secondaryButton}
                {primaryButton}
              </>
            ) : (
              <>
                {primaryButton}
                {secondaryButton}
              </>
            )}
          </Stack>
        </Box>
      }
      {...rest}
    >
      {children && (
        <Box px={2} sx={{ paddingBottom: onPrimaryButtonClick === undefined ? 1 : 0 }}>
          {children}
        </Box>
      )}
    </ModalBase>
  );
});
